import { Article, ArticleContent, Keyword, Question } from "./types";
import { emitCustomEvent } from 'react-custom-events';

class AtNodeDB {
  private _db!: IDBDatabase;
  private _dbInitialized: Promise<void>;

  constructor() {
    this._dbInitialized = new Promise((resolve, reject) => {
      const request = indexedDB.open('atnote', 1);
      request.onerror = (err) => {
        console.log(`IndexedDB error: ${request.error}`, err);
        reject(request.error);
      };
      request.onsuccess = () => {
        this._db = request.result;
        resolve();
      };
      request.onupgradeneeded = () => {
        console.log('Database onupgradeneeded');
        const db = request.result;

        const keywordsStore = db.createObjectStore('Keywords', { keyPath: "Id", autoIncrement: true });
        keywordsStore.createIndex("Name", "Name", { unique: true });
        // const keywords: Keyword[] = [
        //   { Name: 'TypeScript' , Id: 1},
        //   {  Name: 'JavaScript', Id: 2},
        //   { Name: 'Database', Id: 3 }
        // ];
        // keywords.forEach(keyword => keywordsStore.add(keyword));

        const questionsStore = db.createObjectStore('Questions', { keyPath: "Id", autoIncrement: true });
        questionsStore.createIndex("Name", "Name", { unique: true });
        // const questions: Question[] = [
        //   { Name: 'What is TypeScript?', Id: 1 },
        //   { Name: 'What is JavaScript?', Id: 2 },
        //   { Name: 'What is a Database?', Id: 3 }
        // ];
        // questions.forEach(question => questionsStore.add(question));

        const articlesStore = db.createObjectStore('Articles', { keyPath: "Id", autoIncrement: true });
        articlesStore.createIndex("Url", "Url", { unique: true });
        // const articles: Article[] = [
        //   { Title: 'TypeScript Article', Url: 'https://www.typescriptlang.org/', KeywordIDs: [1], QuestionIDs: [1], Id: 1 },
        //   { Title: 'JavaScript Article', Url: 'https://www.javascript.com/', KeywordIDs: [2], QuestionIDs: [2], Id: 2 },
        //   { Title: 'Database Article', Url: 'https://www.mongodb.com/', KeywordIDs: [3], QuestionIDs: [3], Id: 3 }
        // ];
        // articles.forEach(article => articlesStore.add(article));
      };
    });
  }

  public async loadKeywords(search: string, limit: number): Promise<[Keyword[], boolean]> {
    const keywords: Keyword[] = [];
    let thereIsMore = false;

    await this._dbInitialized;
    const transaction = this._db.transaction('Keywords', 'readonly');
    const store = transaction.objectStore('Keywords');
    const request = store.openCursor(null, 'prev');
    request.onsuccess = () => {
      if (thereIsMore) {
        return;
      }
      const cursor = request.result;
      if (cursor) {
        const keyword: Keyword = cursor.value;
        if (search === '' || keyword.Name.toLocaleLowerCase().includes(search.toLocaleLowerCase())) {
          keywords.push(keyword);
        }
        if (limit && keywords.length >= limit) {
          cursor.continue();
          if (cursor) {
            thereIsMore = true;
          }
          return;
        }
        cursor.continue();
      }
    };

    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(null);
      transaction.onerror = () => reject(transaction.error);
    });
    return [keywords, thereIsMore];
  }

  public async insertSummary(title: string, url: string, keywordNames: string[], questionNames: string[]): Promise<void> {
    await this._dbInitialized;

    const keywords = await this.insertKeywords(keywordNames);
    const questions = await this.insertQuestions(questionNames);
    await this.insertArticle(title, url, keywords.map((keyword) => keyword.Id), questions.map((question) => question.Id));
  }

  private async insertQuestions(questions: string[]): Promise<Question[]> {
    const uniqueQuestions = Array.from(new Set(questions));
    await this._dbInitialized;
    const transaction = this._db.transaction('Questions', 'readwrite');
    const store = transaction.objectStore('Questions');

    const existingQuestions: Question[] = [];
    const newQuestions: Question[] = [];

    await Promise.all(uniqueQuestions.map(async (question) => {
      const request = store.index('Name').get(question);
      const result = await new Promise<Question | undefined>((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (result) {
        existingQuestions.push(result);
      } else {
        const newQuestion = { Name: question } as Question;
        const addRequest = store.add(newQuestion);
        const addedQuestion = await new Promise<Question>((resolve, reject) => {
          addRequest.onsuccess = () => {
            newQuestion.Id = addRequest.result as number;
            resolve(newQuestion);
          };
          addRequest.onerror = () => reject(`${addRequest.error}: ${question}`);
        });
        newQuestions.push(addedQuestion);
      }
    }));

    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(null);
      transaction.onerror = () => reject(transaction.error);
    });

    emitCustomEvent('status', `Added ${newQuestions.length} new questions, ${existingQuestions.length} already existed`);

    return [...existingQuestions, ...newQuestions];
  }

  private async insertKeywords(keywords: string[]): Promise<Keyword[]> {
    const uniqueKeywords = Array.from(new Set(keywords));
    await this._dbInitialized;
    const transaction = this._db.transaction('Keywords', 'readwrite');
    const store = transaction.objectStore('Keywords');

    const existingKeywords: Keyword[] = [];
    const newKeywords: Keyword[] = [];

    await Promise.all(uniqueKeywords.map(async (keyword) => {
      const request = store.index('Name').get(keyword);
      const result = await new Promise<Keyword | undefined>((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (result) {
        existingKeywords.push(result);
      } else {
        const newKeyword = { Name: keyword } as Keyword;
        const addRequest = store.add(newKeyword);
        const addedKeyword = await new Promise<Keyword>((resolve, reject) => {
          addRequest.onsuccess = () => {
            newKeyword.Id = addRequest.result as number;
            resolve(newKeyword);
          };
          addRequest.onerror = () => reject(`${addRequest.error}: ${keyword}`);
        });
        newKeywords.push(addedKeyword);
      }
    }));

    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(null);
      transaction.onerror = () => reject(transaction.error);
    });

    emitCustomEvent('status', `Added ${newKeywords.length} new keywords, ${existingKeywords.length} already existed`);

    return [...existingKeywords, ...newKeywords];
  }

  private async insertArticle(title: string, url: string, keywordIDs: number[], questionIDs: number[]): Promise<Article> {
    await this._dbInitialized;

    const existingArticle = await this.getArticleByUrl(url);
    if (existingArticle) {
      emitCustomEvent('status', `Article already exists: ${existingArticle.Title}`);
      return existingArticle;
    }

    const transaction = this._db.transaction('Articles', 'readwrite');
    const store = transaction.objectStore('Articles');

    const article = { Title: title, Url: url, KeywordIDs: keywordIDs, QuestionIDs: questionIDs } as Article;
    const request = store.add(article);
    await new Promise((resolve, reject) => {
      request.onsuccess = () => {
        article.Id = request.result as number;
        resolve(null);
      };
      request.onerror = () => reject(request.error);
    });
    emitCustomEvent('status', `Added article: ${title}`);
    return article;
  }

  public async getArticleByUrl(url: string): Promise<Article | null> {
    await this._dbInitialized;
    const transaction = this._db.transaction('Articles', 'readonly');
    const store = transaction.objectStore('Articles');
    const request = store.index('Url').get(url);

    const article = await new Promise<Article | undefined>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (article) {
      return article;
    }
    else {
      return null;
    }
  }

  public async loadArticle(url: string): Promise<Article | null> {
    return null;
  }

  public async loadArticleContent(sourceAddress: string): Promise<ArticleContent | null> {
    return null;
  }

  public async loadArticles(keywordIds: number[], questionIds: number[], search: string, limit: number): Promise<[Article[], boolean]> {
    const articles: Article[] = [];
    let thereIsMore = false;

    await this._dbInitialized;
    const transaction = this._db.transaction('Articles', 'readonly');
    const store = transaction.objectStore('Articles');
    const request = store.openCursor(null, 'prev');
    request.onsuccess = () => {
      if (thereIsMore) {
        return;
      }
      const cursor = request.result;
      if (cursor) {
        const article: Article = cursor.value;
        if (
          (questionIds.length === 0 || questionIds.every(id => article.QuestionIDs.includes(id))) &&
          (keywordIds.length === 0 || keywordIds.every(id => article.KeywordIDs.includes(id))) &&
          (search === '' || article.Title.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
        ) {
          articles.push(article);
        }
        if (limit && articles.length >= limit) {
          cursor.continue();
          if (cursor) {
            thereIsMore = true;
          }
          return;
        }
        cursor.continue();
      }
    };

    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(null);
      transaction.onerror = () => reject(transaction.error);
    });

    return [articles, thereIsMore];
  }

  public async loadQuestions(keywordIds: number[], search: string, limit = 0): Promise<[Question[], boolean]> {
    const questions: Question[] = [];
    let thereIsMore = false;

    await this._dbInitialized;
    const articlesTransaction = this._db.transaction(['Articles', 'Questions'], 'readonly');
    const articlesStore = articlesTransaction.objectStore('Articles');

    const articleRequest = articlesStore.openCursor();
    const articles: Article[] = [];

    articleRequest.onsuccess = () => {
      const cursor = articleRequest.result;
      if (cursor) {
        const article: Article = cursor.value;
        if (keywordIds.every(id => article.KeywordIDs.includes(id))) {
          articles.push(article);
        }
        cursor.continue();
      }
    };

    await new Promise((resolve, reject) => {
      articlesTransaction.oncomplete = () => resolve(null);
      articlesTransaction.onerror = () => reject(articlesTransaction.error);
    });

    const questionTransaction = this._db.transaction('Questions', 'readonly');
    const questionsStore = questionTransaction.objectStore('Questions');
    const questionRequest = questionsStore.openCursor(null, 'prev');

    questionRequest.onsuccess = () => {
      if (thereIsMore) {
        return;
      }
      const cursor = questionRequest.result;
      if (cursor) {
        const question: Question = cursor.value;
        if (
          (articles.length === 0 || articles.some(article => article.QuestionIDs.includes(question.Id))) &&
          (search === '' || question.Name.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
        ) {
          questions.push(question);
        }
        if (limit && questions.length >= limit) {
          cursor.continue();
          if (cursor) {
            thereIsMore = true;
          }
          return;
        }
        cursor.continue();
      }
    };

    await new Promise((resolve, reject) => {
      questionTransaction.oncomplete = () => resolve(null);
      questionTransaction.onerror = () => reject(questionTransaction.error);
    });

    return [questions, thereIsMore];
  }

  public async loadQuestions_(keywordIds: number[], search: string, limit = 0): Promise<[Question[], boolean]> {
    const questions: Question[] = [];
    let thereIsMore = false;

    await this._dbInitialized;
    const transaction = this._db.transaction('Questions', 'readonly');
    const store = transaction.objectStore('Questions');
    const request = store.openCursor();
    request.onsuccess = () => {
      if (thereIsMore) {
        return;
      }
      const cursor = request.result;
      if (cursor) {
        const question: Question = cursor.value;
        if (
          //(keywordIds.length === 0 || keywordIds.every(id => question.KeywordIDs.includes(id))) &&
          (search === '' || question.Name.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
        ) {
          questions.push(question);
        }
        if (limit && questions.length >= limit) {
          cursor.continue();
          if (cursor) {
            thereIsMore = true;
          }
          return;
        }
        cursor.continue();
      }
    };

    await new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(null);
      transaction.onerror = () => reject(transaction.error);
    });

    return [questions, thereIsMore];
  }
}
const atNodeDB = new AtNodeDB();
export default atNodeDB;